import * as React from "react";
import { Stack, Typography } from "@mui/material";
import { StarRating } from "components/StarRating/StarRating";
import { LinguisticQualityReviewResult } from "gen/clients/llts";
import Divider from "@mui/material/Divider";
import { InfoIcon } from "components/InfoIcon/InfoIcon";

interface Props {
  /**
   * The linguistic quality review result to display (for full display mode with language name and info icon)
   */
  reviewResult?: LinguisticQualityReviewResult;
  /**
   * The name of the language being reviewed (for display purposes, used in full display mode)
   */
  languageName?: string;
  /**
   * The average linguistic quality score across all rated files (for simplified display mode)
   */
  averageScore?: number;
}

const LinguisticQualityReview: React.FC<Props> = ({ reviewResult, languageName, averageScore }) => {
  // Determine which mode to use: full display (with language name and info icon) or simplified (just stars)
  const isFullDisplay = reviewResult !== undefined && languageName !== undefined;
  const score = isFullDisplay ? reviewResult.score : averageScore;

  if (score === undefined) {
    return null;
  }

  return (
    <Stack sx={{ mt: "auto", pt: 2 }}>
      <Divider sx={{ mb: 2 }} />
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1} sx={{ flexWrap: "wrap" }}>
        <Typography variant="body2" color="text.primary">
          Linguistic Quality (AI-Assessed){isFullDisplay && ` - ${languageName}`}
        </Typography>
        <Stack spacing={1} direction="row" alignItems="center" pr={2}>
          <StarRating rating={score} size="small" />
          {isFullDisplay && (
            <>
              <Typography variant="body2" color="text.secondary">
                ({reviewResult.score})
              </Typography>
              {reviewResult.scoreReason && (
                <InfoIcon tooltip={reviewResult.scoreReason} size="small" variant="outlined" />
              )}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export { LinguisticQualityReview };
